import React, { useState, useRef, useEffect } from 'react';
import { FileText, Link, Info, Eye } from 'lucide-react';
import styled from 'styled-components';

const EditorSectionSelector = ({ 
  onSelectSection, 
  onMasterSave,
  onPreview,
  onStatusChange,
  onStatusUpdate,
  isBlogMode = false,
  isProjectMode = false,
  blogStatus = { published: false, isDraft: true },
  projectStatus = { published: false, isDraft: true },
  formData = {} 
}) => {
  const [hoveredSection, setHoveredSection] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [localStatusSelection, setLocalStatusSelection] = useState(
    isBlogMode 
      ? (blogStatus.published ? 'Publish' : 'Draft')
      : (projectStatus.published ? 'Publish' : 'Draft')
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const cardRefs = useRef({});

  // Update local selection when blogStatus or projectStatus changes
  useEffect(() => {
    if (isBlogMode) {
      const currentStatus = blogStatus.published ? 'Publish' : 'Draft';
      setLocalStatusSelection(currentStatus);
    } else if (isProjectMode) {
      const currentStatus = projectStatus.published ? 'Publish' : 'Draft';
      setLocalStatusSelection(currentStatus);
    }
  }, [isBlogMode, isProjectMode, blogStatus.published, projectStatus.published]);

  // Check if status has changed from current status
  const hasStatusChanged = isBlogMode 
    ? localStatusSelection !== (blogStatus.published ? 'Publish' : 'Draft')
    : localStatusSelection !== (projectStatus.published ? 'Publish' : 'Draft');

  // Manual X, Y positions for each tooltip (customize these values)
  const tooltipPositions = {
    'basic-info': {
      x: 800,  // pixels from left edge of screen
      y: -30   // pixels from top edge of screen
    },
    'full-description': {
      x: 800,  // pixels from left edge of screen
      y: -130   // pixels from top edge of screen
    },
    'link-management': {
      x: 800,  // pixels from left edge of screen
      y: -230   // pixels from top edge of screen
    }
  };

  useEffect(() => {
    if (hoveredSection) {
      const position = tooltipPositions[hoveredSection];
      setTooltipPosition({ 
        top: position.y, 
        left: position.x 
      });
    }
  }, [hoveredSection]);

  const sections = [
    {
      id: 'basic-info',
      title: 'Basic Project Information',
      icon: Info,
      color: '#4C1D95',
      fields: [
        'Title',
        'Category',
        'Subcategory',
        'Short Description',
        'Tags',
        'Project Details',
        'Project Link',
        'Gallery Images',
        'Initial Likes'
      ]
    },
    {
      id: 'full-description',
      title: 'Full Description',
      icon: FileText,
      color: '#1E8479',
      fields: [
        'Rich Text Content',
        'Text Formatting',
        'Inline Images',
        'Video Embeds',
        'Audio Players',
        'Quote Blocks',
        'Code Blocks',
        'Tables',
        'FAQ Sections'
      ]
    },
    {
      id: 'link-management',
      title: 'Link Management',
      icon: Link,
      color: '#105652',
      fields: [
        'Internal Links',
        'External Links',
        'Anchor Links',
        'Button Links',
        'Social Media Links',
        'Download Links',
        'Navigation Links',
        'Media Links'
      ]
    }
  ];

  return (
    <div className="w-full h-full flex items-center justify-start p-8 pl-16">
      <div className="max-w-2xl w-full">
        {/* Three Options - Vertical Stack */}
        <div className="space-y-4">
          {sections.map((section, index) => {
            const Icon = section.icon;
            const isHovered = hoveredSection === section.id;

            return (
              <div
                key={section.id}
                className="relative"
                ref={(el) => (cardRefs.current[section.id] = el)}
                onMouseEnter={() => setHoveredSection(section.id)}
                onMouseLeave={() => setHoveredSection(null)}
                style={{
                  animation: `fadeInUp 0.5s ease-out ${index * 0.15}s both`,
                  zIndex: isHovered ? 100 : 1
                }}
              >
                {/* Main Card */}
                <button
                  onClick={() => onSelectSection(section.id)}
                  className="w-full bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-4 flex items-center gap-4 group relative overflow-hidden"
                  style={{
                    border: `2px solid ${isHovered ? section.color : '#e5e7eb'}`,
                    transform: isHovered ? 'translateX(8px)' : 'translateX(0)',
                  }}
                >
                  {/* Icon Container */}
                  <div 
                    className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                    style={{
                      backgroundColor: isHovered ? section.color : `${section.color}15`,
                      transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
                    }}
                  >
                    <Icon 
                      size={28} 
                      style={{ 
                        color: isHovered ? '#FFFAEB' : section.color,
                        strokeWidth: 2.5
                      }} 
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-grow text-left">
                    <h3 
                      className="text-lg font-bold mb-1 transition-colors duration-300"
                      style={{ 
                        color: isHovered ? section.color : '#1f2937'
                      }}
                    >
                      {section.title}
                    </h3>
                    <p className="text-gray-500 text-xs">
                      Click to edit {section.title.toLowerCase()}
                    </p>
                  </div>

                  {/* Arrow Indicator */}
                  <div 
                    className="transition-all duration-300"
                    style={{
                      transform: isHovered ? 'translateX(0)' : 'translateX(-10px)',
                      opacity: isHovered ? 1 : 0
                    }}
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke={section.color}
                      strokeWidth="3"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>

                  {/* Background Pattern */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none"
                    style={{
                      backgroundImage: `repeating-linear-gradient(45deg, ${section.color}, ${section.color} 10px, transparent 10px, transparent 20px)`,
                    }}
                  />
                </button>

                {/* Hover Tooltip - Fields List */}
                <div 
                  className="fixed bg-white rounded-lg shadow-2xl p-4 transition-all duration-300 pointer-events-none"
                  style={{
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? 'scale(1)' : 'scale(0.95)',
                    border: `2px solid ${section.color}`,
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    width: '250px',
                    zIndex: 10000
                  }}
                >
                  {/* Tooltip Arrow - Always on left side */}
                  <div 
                    className="absolute w-3 h-3 transform rotate-45"
                    style={{ 
                      backgroundColor: 'white',
                      borderLeft: `2px solid ${section.color}`,
                      borderBottom: `2px solid ${section.color}`,
                      left: '-6px',
                      top: '20px',
                      display: isHovered ? 'block' : 'none'
                    }}
                  />

                  {/* Tooltip Header */}
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2" style={{ borderColor: section.color }}>
                    <Icon size={18} style={{ color: section.color }} />
                    <h4 className="font-bold text-sm" style={{ color: section.color }}>
                      Available Fields
                    </h4>
                  </div>

                  {/* Fields List */}
                  <ul className="space-y-1">
                    {section.fields.map((field, idx) => (
                      <li 
                        key={idx}
                        className="text-xs text-gray-700 py-1 px-2 rounded hover:bg-gray-50 transition-colors"
                        style={{
                          animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both`
                        }}
                      >
                        • {field}
                      </li>
                    ))}
                  </ul>

                  {/* Tooltip Footer */}
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      {section.fields.length} fields
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex flex-col items-center gap-4">
          {/* Buttons Row */}
          <div className="flex items-center gap-4">
            {/* Master Save Button */}
            <StyledWrapper>
              <button 
                className="button2 master-save-button"
                onClick={onMasterSave}
              >
                {isProjectMode ? 'Save Project' : 'Save'}
              </button>
            </StyledWrapper>
            
            {/* Preview Button */}
            <StyledWrapper>
              <button 
                className="button2 preview-button"
                onClick={onPreview}
                title="Preview project without publishing"
              >
                <Eye size={18} style={{ marginRight: '6px' }} />
                Preview
              </button>
            </StyledWrapper>
            
            {/* Status Dropdown - For Blog and Project Mode */}
            {(isBlogMode || isProjectMode) && (
              <>
                <div className="relative">
                  <select
                    value={localStatusSelection}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('Dropdown changed to:', newValue);
                      setLocalStatusSelection(newValue);
                      // Don't call onStatusChange here - let the update button handle it
                    }}
                    className="px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 bg-white text-gray-700 font-medium cursor-pointer transition-all duration-200"
                    style={{ 
                      borderColor: localStatusSelection === 'Publish' ? '#16a34a' : '#d97706',
                      color: localStatusSelection === 'Publish' ? '#16a34a' : '#d97706',
                      boxShadow: hasStatusChanged ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none'
                    }}
                  >
                    <option value="Draft" style={{ color: '#d97706' }}>Draft</option>
                    <option value="Publish" style={{ color: '#16a34a' }}>Publish</option>
                  </select>
                </div>
                
                {/* Update Status Button */}
                <StyledWrapper>
                  <button 
                    className="button2 update-status-button"
                    onClick={async () => {
                      console.log('Update button clicked, changing to:', localStatusSelection);
                      if (!hasStatusChanged || isUpdating) return;
                      
                      setIsUpdating(true);
                      try {
                        // Call onStatusUpdate directly with the new status
                        // No need to call onStatusChange first - pass status directly
                        if (onStatusUpdate) {
                          await onStatusUpdate(localStatusSelection);
                        }
                      } catch (error) {
                        console.error('Error updating status:', error);
                      } finally {
                        setIsUpdating(false);
                      }
                    }}
                    disabled={!hasStatusChanged || isUpdating}
                    style={{
                      opacity: (!hasStatusChanged || isUpdating) ? 0.6 : 1,
                      cursor: (!hasStatusChanged || isUpdating) ? 'not-allowed' : 'pointer',
                      transform: hasStatusChanged && !isUpdating ? 'scale(1.02)' : 'scale(1)'
                    }}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </button>
                </StyledWrapper>
              </>
            )}
          </div>
          
          {/* Status Information Row - For Blog and Project Mode */}
          {(isBlogMode || isProjectMode) && (
            <div className="text-center">
              {hasStatusChanged && (
                <div className="text-xs transition-all duration-300">
                  <span className="px-3 py-1 rounded-full text-white font-medium" style={{
                    backgroundColor: localStatusSelection === 'Publish' ? '#16a34a' : '#d97706'
                  }}>
                    Click "Update" to change status to {localStatusSelection}
                  </span>
                </div>
              )}
              
              {!hasStatusChanged && (
                <div className="text-xs text-gray-500">
                  Current status: <span className="font-medium" style={{
                    color: (isBlogMode ? blogStatus.published : projectStatus.published) ? '#16a34a' : '#d97706'
                  }}>
                    {(isBlogMode ? blogStatus.published : projectStatus.published) ? 'Published' : 'Draft'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

const StyledWrapper = styled.div`
  .button2 {
    display: inline-block;
    transition: all 0.2s ease-in;
    position: relative;
    overflow: hidden;
    z-index: 1;
    color: #090909;
    padding: 0.7em 1.7em;
    cursor: pointer;
    font-size: 18px;
    border-radius: 0.5em;
    background: #e8e8e8;
    border: 1px solid #e8e8e8;
    box-shadow: 6px 6px 12px #c5c5c5, -6px -6px 12px #ffffff;
  }

  .button2:active {
    color: #666;
    box-shadow: inset 4px 4px 12px #c5c5c5, inset -4px -4px 12px #ffffff;
  }

  .button2:before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%) scaleY(1) scaleX(1.25);
    top: 100%;
    width: 140%;
    height: 180%;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
    display: block;
    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
    z-index: -1;
  }

  .button2:after {
    content: "";
    position: absolute;
    left: 55%;
    transform: translateX(-50%) scaleY(1) scaleX(1.45);
    top: 180%;
    width: 160%;
    height: 190%;
    border-radius: 50%;
    display: block;
    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
    z-index: -1;
  }

  .button2:hover {
    color: #ffffff;
  }

  .button2:hover:before {
    top: -35%;
    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
  }

  .button2:hover:after {
    top: -45%;
    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
  }

  /* Master Save Button - Blue */
  .master-save-button:after {
    background-color: rgba(59, 130, 246, 0.7); /* Blue with 70% opacity */
  }

  .master-save-button:hover {
    border: 1px solid rgba(59, 130, 246, 0.7);
  }

  .master-save-button:hover:before {
    background-color: rgba(59, 130, 246, 0.7);
  }

  .master-save-button:hover:after {
    background-color: rgba(59, 130, 246, 0.7);
  }

  /* Update Status Button - Purple with enhanced states */
  .update-status-button:after {
    background-color: rgba(147, 51, 234, 0.7); /* Purple with 70% opacity */
  }

  .update-status-button:hover:not(:disabled) {
    border: 1px solid rgba(147, 51, 234, 0.7);
  }

  .update-status-button:hover:not(:disabled):before {
    background-color: rgba(147, 51, 234, 0.7);
  }

  .update-status-button:hover:not(:disabled):after {
    background-color: rgba(147, 51, 234, 0.7);
  }

  .update-status-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  .update-status-button:disabled:hover {
    border: 1px solid #e8e8e8;
  }

  .update-status-button:disabled:hover:before,
  .update-status-button:disabled:hover:after {
    background-color: rgba(0, 0, 0, 0.05);
  }

  /* Draft Button - 50% transparent yellow */
  .draft-button:after {
    background-color: rgba(255, 193, 7, 0.5); /* 50% transparent yellow */
  }

  .draft-button:hover {
    border: 1px solid rgba(255, 193, 7, 0.5);
  }

  .draft-button:hover:before {
    background-color: rgba(255, 193, 7, 0.5);
  }

  .draft-button:hover:after {
    background-color: rgba(255, 193, 7, 0.5);
  }

  /* Draft Button Active State */
  .draft-button.active {
    background-color: rgba(255, 193, 7, 0.2);
    border: 2px solid rgba(255, 193, 7, 0.8);
    color: #d97706;
    font-weight: 600;
  }

  /* Publish Button - 50% transparent green */
  .publish-button:after {
    background-color: rgba(40, 167, 69, 0.5); /* 50% transparent green */
  }

  .publish-button:hover {
    border: 1px solid rgba(40, 167, 69, 0.5);
  }

  .publish-button:hover:before {
    background-color: rgba(40, 167, 69, 0.5);
  }

  .publish-button:hover:after {
    background-color: rgba(40, 167, 69, 0.5);
  }

  /* Publish Button Active State */
  .publish-button.active {
    background-color: rgba(40, 167, 69, 0.2);
    border: 2px solid rgba(40, 167, 69, 0.8);
    color: #16a34a;
    font-weight: 600;
  }
  
  /* Preview Button - Teal */
  .preview-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .preview-button:after {
    background-color: rgba(30, 132, 121, 0.7); /* Teal with 70% opacity */
  }

  .preview-button:hover {
    border: 1px solid rgba(30, 132, 121, 0.7);
  }

  .preview-button:hover:before {
    background-color: rgba(30, 132, 121, 0.7);
  }

  .preview-button:hover:after {
    background-color: rgba(30, 132, 121, 0.7);
  }
`;

export default EditorSectionSelector;
